import { useCallback } from "react";
import { notifications } from "@mantine/notifications";
import { useAllocateCredits } from "@pages/Credits/hooks/useAllocateCredits";
import { useUpdateTeamMemberCredits } from "@pages/Credits/hooks/useUpdateTeamMemberCredits";
import useRequestCredits from "@pages/Credits/hooks/useRequestCredits";
import type { CreditOperations } from "../types";

/**
 * Hook that provides credit operation functions with built-in notification handling.
 *
 * This hook wraps the existing credit mutation hooks and adds:
 * 1. Success/error notifications
 * 2. Simplified API for common operations
 * 3. Loading state aggregation
 *
 * @returns Credit operation functions and loading states
 */
export const useCreditOperations = (): CreditOperations => {
  // Use existing credit hooks
  const { mutate: allocateCredits, isPending: isAllocating } =
    useAllocateCredits();

  const { mutate: updateAccessType, isPending: isUpdatingAccess } =
    useUpdateTeamMemberCredits();

  const { handleRequestCredits, isRequestingCredits } = useRequestCredits();

  /**
   * Set credits for a team member (positive for allocate, negative for reclaim).
   *
   * @param userId - The user ID of the team member
   * @param amount - The amount to allocate (positive) or reclaim (negative)
   * @param onSuccess - Optional callback to run after successful allocation
   */
  const handleSetCredits = useCallback(
    (userId: string, amount: number, onSuccess?: () => void) => {
      allocateCredits(
        { userId, amount },
        {
          onSuccess: (response) => {
            const action = amount > 0 ? "allocated" : "reclaimed";
            notifications.show({
              title: "Success",
              message: `Credits ${action} successfully. New user balance: ${response.newUserBalance}`,
              color: "green",
            });
            onSuccess?.();
          },
          onError: (error) => {
            notifications.show({
              title: "Error",
              message:
                error.message || "Failed to update credits. Please try again.",
              color: "red",
            });
          },
        }
      );
    },
    [allocateCredits]
  );

  /**
   * Toggle business use access type for a team member.
   *
   * @param userId - The user ID of the team member
   * @param businessUse - Whether the member can use business credits
   */
  const handleEditAccessType = useCallback(
    (userId: string, businessUse: boolean) => {
      updateAccessType(
        { target_user_id: userId, business_use: businessUse },
        {
          onSuccess: () => {
            const accessType = businessUse ? "Business Use" : "Limited Use";
            notifications.show({
              title: "Success",
              message: `Access type updated to ${accessType}`,
              color: "green",
            });
          },
          onError: (error) => {
            notifications.show({
              title: "Error",
              message:
                error.message ||
                "Failed to update access type. Please try again.",
              color: "red",
            });
          },
        }
      );
    },
    [updateAccessType]
  );

  return {
    handleSetCredits,
    handleEditAccessType,
    handleRequestCredits,
    isAllocating,
    isUpdatingAccess,
    isRequestingCredits,
  };
};
